import type {
    ApplicationSettingThemeColor,
    AutoLayout,
    BackgroundNoImageProtocol,
    BackgroundProtocol,
    Border,
    EdgeValue,
    Shadow,
    VariableADTvalue,
    Veins
} from '@lighthouse/core'
import { BACKGROUND_TYPE, EDGE_MODE } from '@lighthouse/core'

import { applyFillPickerValue4CssColorValue, getGradientCssText } from '../components/FillPicker/utils'
import { getImageFullUrlInApplication } from './image'
import { getFontSizeByPageStyle } from './layout'
import { VEINS_OPTIONS } from './veins'

/** 获取文本的颜色样式 */
export function getTextColorStyle(
    color: BackgroundNoImageProtocol | undefined,
    palettes: ApplicationSettingThemeColor[],
    parseVariableColor?: (v: VariableADTvalue) => string
): React.CSSProperties {
    if (!color) {
        return {}
    }

    if (color.type === BACKGROUND_TYPE.gradient) {
        if (color.gradient) {
            return {
                backgroundImage: getGradientCssText(color.gradient, palettes),
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
            } satisfies React.CSSProperties
        }
        return {}
    }

    if (color.type === BACKGROUND_TYPE.variable) {
        return { color: color.variable && parseVariableColor?.(color.variable) }
    }

    return { color: applyFillPickerValue4CssColorValue(color.color, palettes) }
}

/** 获取背景样式 */
interface GetBackgroundStyleOptions {
    appId?: string
    value?: BackgroundProtocol
    palettes: ApplicationSettingThemeColor[]
    parseVariableImage?: (v: VariableADTvalue) => string
    parseVariableColor?: (v: VariableADTvalue) => string
    cssSuffix?: string
}

export function getBackgroundStyle(options: GetBackgroundStyleOptions): React.CSSProperties {
    const { appId, value, palettes, parseVariableImage, parseVariableColor, cssSuffix = '' } = options
    if (!value) {
        return {}
    }

    if (value.type === BACKGROUND_TYPE.color) {
        const backgroundColor = applyFillPickerValue4CssColorValue(value.color, palettes)
        if (backgroundColor) {
            return { backgroundColor: `${backgroundColor}${cssSuffix}` }
        }
    }
    if (value.type === BACKGROUND_TYPE.gradient && value.gradient) {
        return {
            backgroundImage: `${getGradientCssText(value.gradient, palettes)}${cssSuffix}`,
            backgroundSize: `100% 100%${cssSuffix}`,
            backgroundPosition: `center center${cssSuffix}`,
            backgroundRepeat: `no-repeat${cssSuffix}`
        }
    }
    if (value.type === BACKGROUND_TYPE.image && value.image) {
        return {
            backgroundImage: `url(${getImageFullUrlInApplication(appId, parseVariableImage?.(value.image.resource))})${cssSuffix}`,
            backgroundSize: `${value.image.fit}${cssSuffix}`,
            backgroundPosition: `center center${cssSuffix}`,
            backgroundRepeat: `no-repeat${cssSuffix}`
        }
    }

    if (value.type === BACKGROUND_TYPE.variable && value.variable) {
        return {
            backgroundColor: parseVariableColor?.(value.variable)
        }
    }

    return {}
}

/** 获取纹理样式 */
export function getVeinsStyle(veins: Veins | undefined, palettes: ApplicationSettingThemeColor[]): React.CSSProperties {
    if (!veins) {
        return {}
    }
    const veinsConfigure = VEINS_OPTIONS.find(item => item.value === veins.value)
    if (!veinsConfigure) {
        return {}
    }

    let { raw } = veinsConfigure
    if (veins.color) {
        const color = applyFillPickerValue4CssColorValue(veins.color, palettes) || '#000'
        raw = raw.replaceAll('currentColor', color)
        raw = raw.replaceAll(/stroke="#\w+"/g, `stroke="${color}"`)
    }
    if (veins.strokeWidth) {
        raw = raw.replaceAll(/stroke-width="[\w.]+"/g, `stroke-width="${veins.strokeWidth}px"`)
        raw = raw.replaceAll(/<circle(.+\s*)+\/>/g, match => match.replaceAll(/r="[\w%.]+"/g, `r="${veins.strokeWidth}%"`))
        raw = raw.replaceAll(/<rect(.+\s*)+\/>/g, match => match.replaceAll(/(width|height)="[\w%.]+"/g, `$1="${veins.strokeWidth}%"`))
    }

    return {
        backgroundImage: `url('data:image/svg+xml;charset=utf8,${encodeURIComponent(raw)}')`,
        backgroundSize: `${Math.ceil((512 / 100) * (veins.size || 100))}px`,
        backgroundPosition: 'center center',
        backgroundRepeat: 'repeat'
    }
}

/**
 * 合并背景样式
 * @param styles
 * @returns
 */
export function combineBackgroundStyle(styles: React.CSSProperties[]): React.CSSProperties {
    const backgroundColor = styles.find(item => item.backgroundColor)?.backgroundColor
    return {
        backgroundColor,
        backgroundImage: styles
            .map(item => item.backgroundImage)
            .filter(Boolean)
            .join(','),
        backgroundSize: styles
            .map(item => item.backgroundSize)
            .filter(Boolean)
            .join(','),
        backgroundPosition: styles
            .map(item => item.backgroundPosition)
            .filter(Boolean)
            .join(','),
        backgroundRepeat: styles
            .map(item => item.backgroundRepeat)
            .filter(Boolean)
            .join(',')
    }
}

export function getPaddingBottomStyle(padding: AutoLayout['padding'], isBuilder?: boolean): string {
    const unit = padding?.unit || 'px'
    const [_, __, ___, paddingBottom = 0] = padding?.size || []
    if (isBuilder && unit === 'rem') {
        const fontSize = getFontSizeByPageStyle()
        if (fontSize) {
            return `${paddingBottom * fontSize}px`
        }
    }
    return `${paddingBottom}${unit}`
}

export function getPaddingTopStyle(padding: AutoLayout['padding'], isBuilder?: boolean): string {
    const unit = padding?.unit || 'px'
    const [, paddingTop = 0] = padding?.size || []
    if (isBuilder && unit === 'rem') {
        const fontSize = getFontSizeByPageStyle()
        if (fontSize) {
            return `${paddingTop * fontSize}px`
        }
    }
    return `${paddingTop}${unit}`
}

/** 获取内边距样式 */
export function getPaddingStyle(padding: AutoLayout['padding'], isBuilder?: boolean): React.CSSProperties {
    const unit = padding?.unit || 'px'
    const [paddingLeft = 0, paddingTop = 0, paddingRight = 0, paddingBottom = 0] = padding?.size || []

    if (isBuilder && unit === 'rem') {
        const fontSize = getFontSizeByPageStyle()
        if (fontSize) {
            return {
                padding: `${paddingTop * fontSize}px ${paddingRight * fontSize}px ${paddingBottom * fontSize}px ${paddingLeft * fontSize}px`
            }
        }
    }

    return { padding: `${paddingTop}${unit} ${paddingRight}${unit} ${paddingBottom}${unit} ${paddingLeft}${unit}` }
}

/** 获取阴影样式 */
export function getShadowStyle(shadow: Shadow | undefined, palettes: ApplicationSettingThemeColor[]): React.CSSProperties {
    const { color, x: shadowOffsetX = 0, y: shadowOffsetY = 0, blur: shadowBlur = 0, diffusion: shadowDiffusion = 0 } = shadow ?? {}

    const shadowColor = color && applyFillPickerValue4CssColorValue(color, palettes)

    return shadowColor ? { boxShadow: `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowDiffusion}px ${shadowColor}` } : {}
}

/** 获取圆角样式 */
export function getRadiusStyle(value: EdgeValue | undefined): React.CSSProperties {
    if (!value) {
        return {}
    }

    const [lt, rt, lb, rb] = value
    return {
        borderTopLeftRadius: `${lt || 0}px`,
        borderTopRightRadius: `${rt || 0}px`,
        borderBottomLeftRadius: `${lb || 0}px`,
        borderBottomRightRadius: `${rb || 0}px`
    }
    // return { borderRadius: `${lt || 0}px  ${rt || 0}px ${rb || 0}px ${lb || 0}px` }
}

/** 获取边框样式 */
export function getBorderStyle(border: Border | undefined, palettes: ApplicationSettingThemeColor[], cssSuffix = ''): React.CSSProperties {
    const { type: borderType, color, mode: borderMode, all: borderAll, each: borderEach } = border ?? {}
    const [borderLeft = 0, borderTop = 0, borderRight = 0, borderBottom = 0] =
        borderMode === EDGE_MODE.each ? borderEach || [] : Array.from({ length: 4 }).map(() => borderAll || 0)

    const borderColor = color && applyFillPickerValue4CssColorValue(color, palettes)

    if (!borderColor) {
        return {}
    }
    return {
        borderLeft: borderLeft ? `${borderLeft}px solid ${borderColor}${cssSuffix}` : `none${cssSuffix}`,
        borderTop: borderTop ? `${borderTop}px solid ${borderColor}${cssSuffix}` : `none${cssSuffix}`,
        borderRight: borderRight ? `${borderRight}px solid ${borderColor}${cssSuffix}` : `none${cssSuffix}`,
        borderBottom: borderBottom ? `${borderBottom}px solid ${borderColor}${cssSuffix}` : `none${cssSuffix}`
    }
}

export function mergeBackgroundStyles(styles: React.CSSProperties): React.CSSProperties {
    const { backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat, backgroundColor } = styles

    // 如果存在 backgroundImage, backgroundSize, backgroundPosition 和 backgroundRepeat，
    // 将它们合并为一个单一的 background 属性
    if (backgroundImage?.includes('url')) {
        const backgroundParts = [
            backgroundImage || '', // 背景图片
            backgroundPosition || 'center center', // 背景位置
            backgroundSize || '100% 100%', // 背景大小
            backgroundRepeat || 'no-repeat' // 背景重复
        ]

        return {
            ...styles,
            backgroundImage: backgroundParts.join(' ') // 将这些属性合并为一个 background
        }
    }

    return styles // 如果没有相关属性，直接返回原样
}
